import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { checkIdentity } from "@/lib/identityApi";
import { formatApiError } from "@/lib/api";
import { isCapabilitiesModuleEnabled } from "@/lib/capabilitiesAdapters";
import { useCapabilities } from "@/lib/useCapabilities";
import { toast } from "sonner";

function art69Label(art69) {
  if (!art69) return "—";
  if (art69.listed) return "Listado Art. 69";
  if (art69.status === "LIMPIO") return "Limpio";
  if (art69.status === "HISTORICO") return "Histórico (sin riesgo vigente)";
  if (art69.status === "SIN_CONSULTA_PREVIA") return "Lista EFOS no sincronizada";
  if (art69.status === "SIN_DATOS") return "Sin datos locales";
  return art69.status;
}

/**
 * Diálogo reutilizable Check-ID (RFC y/o CURP) — sin rediseño, componentes existentes.
 */
export default function IdentityCheckDialog({
  open,
  onOpenChange,
  rfc = "",
  curp = "",
  title = "Validación Check-ID",
}) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runCheck = async () => {
    const rfcN = (rfc || "").trim();
    const curpN = (curp || "").trim();
    if (!rfcN && !curpN) {
      toast.error("Indique RFC o CURP para validar");
      return;
    }
    setLoading(true);
    try {
      const data = await checkIdentity({ rfc: rfcN, curp: curpN });
      setResult(data);
      if (!data?.success) {
        toast.warning("Validación con observaciones — revise el detalle");
      }
    } catch (err) {
      setResult(null);
      toast.error(formatApiError(err, "No se pudo validar identidad"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) setResult(null);
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#0F2D5C]">{title}</DialogTitle>
        </DialogHeader>
        {!result ? (
          <div className="space-y-3 text-sm">
            {rfc ? (
              <p className="m-0">
                <span className="text-slate-500">RFC:</span>{" "}
                <span className="font-mono font-medium">{rfc.toUpperCase()}</span>
              </p>
            ) : null}
            {curp ? (
              <p className="m-0">
                <span className="text-slate-500">CURP:</span>{" "}
                <span className="font-mono font-medium">{curp.toUpperCase()}</span>
              </p>
            ) : null}
            <Button
              type="button"
              size="sm"
              className="bg-[#0F2D5C] hover:bg-[#0a2247]"
              disabled={loading}
              onClick={runCheck}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Ejecutar validación
            </Button>
          </div>
        ) : (
          <div className="space-y-3 text-sm text-slate-700">
            {result.query?.rfc ? (
              <div className="rounded-md border border-slate-200 p-3 space-y-1">
                <p className="font-semibold text-[#0F2D5C] m-0">RFC</p>
                <p className="m-0 font-mono text-xs">{result.query.rfc}</p>
                {result.sat?.razon_social ? (
                  <p className="m-0">
                    <strong>Razón social:</strong> {result.sat.razon_social}
                  </p>
                ) : null}
                {result.sat?.codigo_postal ? (
                  <p className="m-0">
                    <strong>C.P.:</strong> {result.sat.codigo_postal}
                  </p>
                ) : null}
                {result.sat?.regimen_fiscal ? (
                  <p className="m-0">
                    <strong>Régimen:</strong> {result.sat.regimen_fiscal}
                  </p>
                ) : null}
                {result.sat?.confidence > 0 && result.sat.confidence < 100 ? (
                  <p className="m-0 text-xs text-amber-800">
                    Caché local ({result.sat.source === "workspace" ? "espacio de trabajo" : (result.sat.source || "espacio de trabajo")}, confianza{" "}
                    {result.sat.confidence}%)
                  </p>
                ) : null}
                <p className="m-0">
                  <strong>Artículo 69:</strong> {art69Label(result.art69)}
                </p>
              </div>
            ) : null}
            {result.query?.curp ? (
              <div className="rounded-md border border-slate-200 p-3 space-y-1">
                <p className="font-semibold text-[#0F2D5C] m-0">CURP</p>
                <p className="m-0 font-mono text-xs">{result.query.curp}</p>
                {result.curp_data?.extracted?.fecha_nacimiento ? (
                  <p className="m-0">
                    <strong>Nacimiento:</strong> {result.curp_data.extracted.fecha_nacimiento}
                  </p>
                ) : null}
                {result.curp_data?.extracted?.sexo ? (
                  <p className="m-0">
                    <strong>Sexo:</strong> {result.curp_data.extracted.sexo}
                  </p>
                ) : null}
                <p className="m-0">
                  <strong>NSS:</strong> {result.imss?.nss || "Pendiente (Fase 5)"}
                </p>
              </div>
            ) : null}
            {result.offline || result.sat?.status === "OFFLINE_ESTRUCTURAL" ? (
              <p className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-sm px-2 py-1.5 m-0">
                Modo sin conexión: solo validación estructural local.
              </p>
            ) : null}
            {result.warnings?.length ? (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5 m-0">
                {result.warnings[0]}
              </p>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Botón compacto que abre el diálogo Check-ID.
 */
export function IdentityValidateButton({
  rfc = "",
  curp = "",
  label,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const { loading, isPlatformDesigner, subscription, data: capabilities } = useCapabilities();
  const btnLabel = label || (curp && !rfc ? "Validar CURP" : "Validar RFC");
  const hasValue = Boolean((rfc || "").trim() || (curp || "").trim());

  const { disabled, tooltip } = useMemo(() => {
    if (loading) {
      return { disabled: true, tooltip: "" };
    }
    if (isPlatformDesigner) {
      return { disabled: !hasValue, tooltip: "" };
    }
    const subscriptionOk = subscription?.ok !== false;
    const checkIdEnabled = isCapabilitiesModuleEnabled(capabilities, "check_id") === true;
    if (!subscriptionOk) {
      return {
        disabled: !hasValue,
        tooltip: "Modo sin conexión: solo validación estructural",
      };
    }
    if (!checkIdEnabled) {
      return {
        disabled: true,
        tooltip: "Módulo Check-ID no contratado",
      };
    }
    return { disabled: !hasValue, tooltip: "" };
  }, [loading, isPlatformDesigner, subscription, capabilities, hasValue]);

  if (loading) {
    return null;
  }

  const button = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={`h-7 text-xs rounded-sm ${className}`}
      onClick={() => setOpen(true)}
      disabled={disabled}
    >
      {btnLabel}
    </Button>
  );

  return (
    <>
      {tooltip ? (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">{button}</span>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs max-w-[220px]">
              {tooltip}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        button
      )}
      <IdentityCheckDialog
        open={open}
        onOpenChange={setOpen}
        rfc={rfc}
        curp={curp}
        title={btnLabel}
      />
    </>
  );
}
